/**
 * Карточка уведомления — общая для колокольчика и вкладки «Уведомления».
 *
 * Смысл карточки (вид, причина, русская категория) приходит с сервера;
 * компонент только показывает и предлагает три разных действия:
 * «прочитано» (остаётся в ленте), «убрать» (уходит в архив), «отложить».
 */

import {
    AlarmClock, Archive, ArrowRight, Bell, Check, ClipboardCheck, GraduationCap,
    Heart, Monitor, RotateCcw, ShieldAlert, UserPlus, Users,
} from 'lucide-react'

import { deadlineLabel, isOverdue, snoozePresets } from '../layout/notificationView'
import './notification-card.css'

// Иконка по категории — Lucide, а не цветной emoji из полезной нагрузки.
const CATEGORY_ICONS = {
    recruiting: UserPlus,
    approvals: ClipboardCheck,
    learning: GraduationCap,
    hr_events: Users,
    it_events: Monitor,
    kudos: Heart,
    system: ShieldAlert,
}

const CRM_ORDER_URL = /(https:\/\/crm\.senateexchange\.com\/orders\/\d+\/)(?=$|\s|[)\],.;!?])/g
const HAS_CRM_ORDER_URL = /https:\/\/crm\.senateexchange\.com\/orders\/\d+\/(?=$|\s|[)\],.;!?])/
const EXACT_CRM_ORDER_URL = /^https:\/\/crm\.senateexchange\.com\/orders\/\d+\/$/

export function NotificationMessage({ message }) {
    if (typeof message !== 'string') return null
    return message.split(CRM_ORDER_URL).map((part, index) => (
        EXACT_CRM_ORDER_URL.test(part)
            ? (
                <a
                    key={`${part}-${index}`}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {part}
                </a>
            )
            : part
    ))
}

function timeLabel(iso) {
    if (!iso) return ''
    return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}

export default function NotificationCard({
    item,
    onOpen,
    onRead,
    onArchive,
    onSnooze,
    onUnsnooze,
    snoozeOpen,
    onToggleSnooze,
    selected,
    onToggleSelect,
}) {
    const Icon = CATEGORY_ICONS[item.category] || Bell
    const overdue = isOverdue(item)
    const deadline = deadlineLabel(item.deadline_at)
    const messageHasLinks = typeof item.message === 'string' && HAS_CRM_ORDER_URL.test(item.message)
    const accent = item.priority === 'critical'
        ? 'var(--color-danger)'
        : overdue || item.priority === 'high'
            ? 'var(--color-warning)'
            : 'var(--color-accent)'

    return (
        <div className={`notif-card${item.is_read ? '' : ' notif-card--unread'}`}>
            {onToggleSelect && (
                <input
                    type="checkbox"
                    className="notif-card__select"
                    checked={Boolean(selected)}
                    onChange={() => onToggleSelect(item)}
                    aria-label={`Выбрать: ${item.title}`}
                />
            )}
            <span className="notif-card__icon" style={{ background: 'var(--color-accent-bg)', color: accent }}>
                <Icon size={16} />
            </span>
            <div className="notif-card__body">
                <button type="button" className="notif-card__title" onClick={() => onOpen(item)}>
                    {item.title}
                    {item.batch_count > 1 && <span className="notif-card__batch">×{item.batch_count}</span>}
                </button>
                {item.message && (
                    <div className={`notif-card__detail${messageHasLinks ? ' notif-card__detail--links' : ''}`}>
                        <NotificationMessage message={item.message} />
                    </div>
                )}
                <div className="notif-card__meta">
                    {item.category_label && <span>{item.category_label}</span>}
                    {item.reason && <span className="notif-card__reason">{item.reason}</span>}
                    {deadline && (
                        <span className={overdue ? 'notif-card__deadline notif-card__deadline--overdue' : 'notif-card__deadline'}>
                            {deadline}
                        </span>
                    )}
                    <span className="notif-card__time">{timeLabel(item.created_at)}</span>
                </div>
                {item.action_url && (
                    <button type="button" className="btn btn--secondary btn--sm notif-card__cta" onClick={() => onOpen(item)}>
                        {item.quick_action?.label || 'Открыть'} <ArrowRight size={13} />
                    </button>
                )}
            </div>
            <div className="notif-card__tools">
                {onRead && !item.is_read && (
                    <button type="button" title="Прочитано" aria-label="Прочитано" onClick={() => onRead(item)}>
                        <Check size={14} />
                    </button>
                )}
                {onUnsnooze && item.snoozed_until && (
                    <button type="button" title="Вернуть в ленту" aria-label="Вернуть в ленту" onClick={() => onUnsnooze(item)}>
                        <RotateCcw size={14} />
                    </button>
                )}
                {onToggleSnooze && !item.snoozed_until && (
                    <button type="button" title="Отложить" aria-label="Отложить" onClick={() => onToggleSnooze(item)}>
                        <AlarmClock size={14} />
                    </button>
                )}
                {onArchive && !item.is_archived && (
                    <button type="button" title="Убрать" aria-label="Убрать" onClick={() => onArchive(item)}>
                        <Archive size={14} />
                    </button>
                )}
            </div>
            {snoozeOpen && (
                <div className="notif-card__snooze" role="group" aria-label="Отложить до">
                    {snoozePresets().map(preset => (
                        <button key={preset.key} type="button" onClick={() => onSnooze(item, preset.until)}>
                            {preset.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}
